<script setup lang="ts">
import { ref, computed } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useToastStore } from '../stores/toast'
import { useI18n } from 'vue-i18n'
import clubService from '../services/club.service'

const route = useRoute()
const router = useRouter()
const toastStore = useToastStore()
const { t } = useI18n()

// Get club UUID from route
const clubUuid = computed(() => route.params.uuid as string)

// Form fields
const accountName = ref('')
const bankName = ref('')
const accountNumber = ref('')
const swift = ref('')
const currency = ref('PLN')

const loading = ref(false)
const error = ref<string | null>(null)
const validationErrors = ref<Record<string, string[]>>({})

// Handle form submission
const handleSubmit = async () => {
  if (!clubUuid.value) {
    error.value = t('bankAccounts.errorNoClubUuid')
    return
  }

  loading.value = true
  error.value = null
  validationErrors.value = {}

  try {
    const data = {
      account_name: accountName.value.trim(),
      bank_name: bankName.value.trim(),
      account_number: accountNumber.value.trim(),
      swift: swift.value.trim() || null,
      currency: currency.value
    }

    await clubService.createBankAccount(clubUuid.value, data)

    toastStore.success(t('bankAccounts.bankAccountCreated'))
    router.push(`/club/${clubUuid.value}/bank-accounts`)
  } catch (err: any) {
    console.error('Create bank account error:', err)

    // Handle validation errors
    if (err.response?.data?.errors) {
      validationErrors.value = err.response.data.errors
    } else {
      error.value = err.response?.data?.message || t('bankAccounts.errorCreatingBankAccount')
    }
  } finally {
    loading.value = false
  }
}

// Get validation error for field
const getFieldError = (fieldName: string): string | null => {
  const errors = validationErrors.value[fieldName]
  return errors && errors.length > 0 ? errors[0] : null
}

// Check if field has error
const hasFieldError = (fieldName: string): boolean => {
  return !!validationErrors.value[fieldName]
}

// Handle cancel
const handleCancel = () => {
  router.push(`/club/${clubUuid.value}/bank-accounts`)
}
</script>

<template>
  <main class="bg-gray-50 min-h-screen">
    <div class="max-w-3xl mx-auto px-4 py-4">
      <!-- Back Button -->
      <div class="mb-4">
        <button
          @click="handleCancel"
          class="inline-flex items-center gap-2 text-sm text-blue-600 hover:text-blue-800 hover:underline"
        >
          <svg class="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
            <path fill-rule="evenodd" d="M9.707 16.707a1 1 0 01-1.414 0l-6-6a1 1 0 010-1.414l6-6a1 1 0 011.414 1.414L5.414 9H17a1 1 0 110 2H5.414l4.293 4.293a1 1 0 010 1.414z" clip-rule="evenodd"/>
          </svg>
          {{ t('bankAccounts.backToList') }}
        </button>
      </div>

      <!-- Page Header -->
      <div class="mb-4">
        <h1 class="text-2xl font-bold text-gray-900">{{ t('bankAccounts.addBankAccount') }}</h1>
        <p class="text-sm text-gray-600 mt-1">{{ t('bankAccounts.addBankAccountSubtitle') }}</p>
      </div>

      <!-- Error Message -->
      <div v-if="error" class="mb-4 bg-red-50 border-2 border-red-300 rounded p-3">
        <p class="text-sm text-red-800">{{ error }}</p>
      </div>

      <!-- Add Form -->
      <form @submit.prevent="handleSubmit" class="bg-white border-2 border-gray-300 rounded shadow-sm p-4">
        <!-- Nazwa konta -->
        <div class="mb-3">
          <label for="account_name" class="block text-sm font-medium text-gray-700 mb-1">
            {{ t('bankAccounts.accountName') }} <span class="text-red-600">{{ t('common.required') }}</span>
          </label>
          <input
            v-model="accountName"
            type="text"
            id="account_name"
            required
            :class="[
              'w-full px-3 py-2 border rounded text-sm focus:outline-none focus:border-blue-600',
              hasFieldError('account_name') ? 'border-red-500' : 'border-gray-300'
            ]"
            :placeholder="t('bankAccounts.accountNamePlaceholder')"
          >
          <p v-if="getFieldError('account_name')" class="text-xs text-red-600 mt-1">
            {{ getFieldError('account_name') }}
          </p>
        </div>

        <!-- Nazwa banku -->
        <div class="mb-3">
          <label for="bank_name" class="block text-sm font-medium text-gray-700 mb-1">
            {{ t('bankAccounts.bankName') }} <span class="text-red-600">{{ t('common.required') }}</span>
          </label>
          <input
            v-model="bankName"
            type="text"
            id="bank_name"
            required
            :class="[
              'w-full px-3 py-2 border rounded text-sm focus:outline-none focus:border-blue-600',
              hasFieldError('bank_name') ? 'border-red-500' : 'border-gray-300'
            ]"
            :placeholder="t('bankAccounts.bankNamePlaceholder')"
          >
          <p v-if="getFieldError('bank_name')" class="text-xs text-red-600 mt-1">
            {{ getFieldError('bank_name') }}
          </p>
        </div>

        <!-- Numer konta -->
        <div class="mb-3">
          <label for="account_number" class="block text-sm font-medium text-gray-700 mb-1">
            {{ t('bankAccounts.accountNumber') }} <span class="text-red-600">{{ t('common.required') }}</span>
          </label>
          <input
            v-model="accountNumber"
            type="text"
            id="account_number"
            required
            maxlength="34"
            :class="[
              'w-full px-3 py-2 border rounded text-sm focus:outline-none focus:border-blue-600 font-mono',
              hasFieldError('account_number') ? 'border-red-500' : 'border-gray-300'
            ]"
            :placeholder="t('bankAccounts.accountNumberPlaceholder')"
          >
          <p class="text-xs text-gray-500 mt-1">{{ t('bankAccounts.accountNumberHint') }}</p>
          <p v-if="getFieldError('account_number')" class="text-xs text-red-600 mt-1">
            {{ getFieldError('account_number') }}
          </p>
        </div>

        <!-- SWIFT -->
        <div class="mb-3">
          <label for="swift" class="block text-sm font-medium text-gray-700 mb-1">
            SWIFT/BIC
          </label>
          <input
            v-model="swift"
            type="text"
            id="swift"
            maxlength="15"
            :class="[
              'w-full px-3 py-2 border rounded text-sm focus:outline-none focus:border-blue-600 font-mono uppercase',
              hasFieldError('swift') ? 'border-red-500' : 'border-gray-300'
            ]"
            :placeholder="t('bankAccounts.swiftPlaceholder')"
          >
          <p class="text-xs text-gray-500 mt-1">{{ t('bankAccounts.swiftHint') }}</p>
          <p v-if="getFieldError('swift')" class="text-xs text-red-600 mt-1">
            {{ getFieldError('swift') }}
          </p>
        </div>

        <!-- Currency -->
        <div class="mb-3">
          <label for="currency" class="block text-sm font-medium text-gray-700 mb-1">
            {{ t('bankAccounts.currency') }} <span class="text-red-600">{{ t('common.required') }}</span>
          </label>
          <select
            v-model="currency"
            id="currency"
            required
            :class="[
              'w-full px-3 py-2 border rounded text-sm focus:outline-none focus:border-blue-600',
              hasFieldError('currency') ? 'border-red-500' : 'border-gray-300'
            ]"
          >
            <option v-for="code in ['PLN', 'EUR', 'GBP', 'USD', 'CHF', 'CZK', 'DKK', 'NOK', 'SEK', 'RON', 'HRK', 'LTL']" :key="code" :value="code">{{ code }} - {{ t(`currencies.${code}`) }}</option>
          </select>
          <p v-if="getFieldError('currency')" class="text-xs text-red-600 mt-1">
            {{ getFieldError('currency') }}
          </p>
        </div>

        <!-- Submit Section -->
        <div class="pt-4 border-t border-gray-200 flex justify-end gap-3">
          <button
            type="button"
            @click="handleCancel"
            class="py-2 px-4 bg-gray-100 text-gray-700 font-medium rounded hover:bg-gray-200 border border-gray-300"
          >
            {{ t('common.cancel') }}
          </button>
          <button
            type="submit"
            :disabled="loading"
            class="py-2 px-4 bg-green-600 text-white font-medium rounded hover:bg-green-700 transition disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {{ loading ? t('bankAccounts.adding') : t('bankAccounts.addBankAccount') }}
          </button>
        </div>
      </form>
    </div>
  </main>
</template>
